import React, { useEffect, useState } from "react";
import { Cake, Gift, Check, MessageCircle, CalendarDays, ChevronDown, ChevronUp } from "lucide-react";
import { sharedGet } from "@/lib/sharedGet";
import { whenFeedReady } from "@/lib/lowPriority";
import { useApp } from "@/context/AppContext";
import { BirthdaysDialog, firstNameOf } from "@/components/BirthdaysDialog";
import { UpcomingBirthdays } from "@/components/UpcomingBirthdays";
import { DismissX } from "@/components/smart/DismissX";

const NO_UPCOMING = { enabled: false, days: 7, count: 0 };

// Données du widget (exportées pour les cartes intelligentes du fil) ; `enabled=false` → aucune requête.
export const useBirthdays = (user, enabled = true) => {
  const [birthdays, setBirthdays] = useState([]);
  const [upcoming, setUpcoming] = useState(NO_UPCOMING);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    if (!user) { setLoaded(true); return undefined; }
    // Après le 1er rendu du fil ; une seule requête même si le widget est monté deux fois (PC + mobile).
    return whenFeedReady(() => {
      sharedGet("/users/birthdays/today", { ttl: 60000 })
        .then((data) => { setBirthdays((data.birthdays || []).slice(0, 12)); setUpcoming({ ...NO_UPCOMING, ...(data.upcoming || {}) }); })
        .catch(() => {})
        .finally(() => setLoaded(true));
    });
  }, [user, enabled]);
  const markWished = (id) => setBirthdays((prev) => prev.map((b) => (b.id === id ? { ...b, wished: true } : b)));
  const hasUpcoming = upcoming.enabled && upcoming.count > 0;
  return { birthdays, upcoming, loaded, markWished, hasUpcoming };
};

export const BirthdayWidget = ({ hideWhenEmpty = false, data = null, onDismiss = null }) => {
  const { t, user, openChat } = useApp();
  const own = useBirthdays(user, !data);
  const { birthdays, upcoming, loaded, markWished, hasUpcoming } = data || own;
  const [open, setOpen] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const me = birthdays.find((b) => b.is_me);
  const others = birthdays.filter((b) => !b.is_me);
  const pending = others.filter((b) => !b.wished);
  const wished = others.filter((b) => b.wished);

  if (hideWhenEmpty && (!loaded || (birthdays.length === 0 && !hasUpcoming))) return null;

  const summary = () => {
    if (me && pending.length === 0) return <>{t("birthdays.summary_mine", "C'est votre anniversaire aujourd'hui 🎂")}</>;
    const extra = pending.length - 1;
    const tpl = (extra > 0
      ? t("birthdays.summary_many", "C'est l'anniversaire de {name} et {count} autre(s) personne(s) aujourd'hui.")
      : t("birthdays.summary_one", "C'est l'anniversaire de {name} aujourd'hui.")
    ).replace("{count}", extra);
    const [before, after] = tpl.split("{name}");
    return (
      <>
        {before}<b>{pending[0]?.name}</b>{after}
        {me && <> {t("birthdays.yours_too", "— et le vôtre 🎂")}</>}
      </>
    );
  };

  // « Afficher / Masquer les anniversaires à venir » (façon Facebook), sous le contenu du jour.
  const upcomingToggle = hasUpcoming && (
    <button
      type="button"
      onClick={() => setShowUpcoming((v) => !v)}
      className="mt-4 w-full flex items-center justify-center gap-1.5 text-xs font-semibold rounded-full py-2 transition-colors hover:bg-[rgba(0,0,0,0.04)]"
      style={{ color: "var(--yalola-primary)", background: "var(--yalola-surface-alt)" }}
      aria-expanded={showUpcoming}
      data-testid="birthdays-upcoming-toggle"
    >
      <CalendarDays size={14} />
      {showUpcoming ? t("birthdays.upcoming_hide", "Masquer les anniversaires à venir") : t("birthdays.upcoming_link", "Afficher les anniversaires à venir")}
      {showUpcoming ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
    </button>
  );

  return (
    <div className="yalola-card p-5" data-testid="birthdays-widget" data-upcoming={hasUpcoming ? upcoming.count : 0}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-secondary)" }}>
        <Cake size={14} /> {t("birthdays.title", "Anniversaires")}
        <span className="flex-1" />
        <DismissX onClick={onDismiss} testId="birthdays-dismiss" label={t("smart_cards.dismiss", "Masquer")} />
      </div>
      {!loaded && user ? (
        <div className="mt-3 space-y-2 animate-pulse" data-testid="birthdays-skeleton">
          <div style={{ width: "82%", height: 12, borderRadius: 6, background: "rgba(128,128,128,0.16)" }} />
          <div style={{ width: "55%", height: 12, borderRadius: 6, background: "rgba(128,128,128,0.16)" }} />
        </div>
      ) : birthdays.length === 0 ? (
        hasUpcoming ? (
          // Aucun anniversaire aujourd'hui → carte compacte « N anniversaire(s) dans les X prochains jours · Afficher ».
          <button
            type="button"
            onClick={() => setShowUpcoming((v) => !v)}
            className="mt-3 w-full flex items-center gap-3 text-left rounded-xl p-2 -m-2 transition-colors hover:bg-[rgba(0,0,0,0.04)]"
            aria-expanded={showUpcoming}
            data-testid="birthdays-upcoming-compact"
          >
            <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)" }}>
              <CalendarDays size={18} style={{ color: "var(--yalola-primary)" }} />
            </span>
            <span className="flex-1 text-sm leading-snug" style={{ color: "var(--yalola-text)" }}>
              {t("birthdays.upcoming_count", "{count} anniversaire(s) dans les {days} prochains jours.").replace("{count}", upcoming.count).replace("{days}", upcoming.days)}
            </span>
            <span className="text-xs font-semibold shrink-0 inline-flex items-center gap-0.5" style={{ color: "var(--yalola-primary)" }}>
              {showUpcoming ? t("birthdays.hide", "Masquer") : t("birthdays.show", "Afficher")} {showUpcoming ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          </button>
        ) : (
          <p className="mt-3 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="birthdays-empty">
            {t("birthdays.empty", "Aucun anniversaire aujourd'hui.")}
          </p>
        )
      ) : (
        <>
          {(pending.length > 0 || me) && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-3 w-full flex items-start gap-3 text-left rounded-xl p-2 -m-2 transition-colors hover:bg-[rgba(0,0,0,0.04)]"
              data-testid="birthdays-summary"
            >
              <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)" }}>
                <Gift size={18} style={{ color: "var(--yalola-primary)" }} />
              </span>
              <span className="text-sm leading-snug" style={{ color: "var(--yalola-text)" }}>{summary()}</span>
            </button>
          )}
          {/* Façon Facebook : « Vous avez souhaité un joyeux anniversaire à X · Voir la conversation » — jamais deux fois. */}
          {wished.map((b) => (
            <div key={b.id} className={`flex items-start gap-3 ${(pending.length > 0 || me) ? "mt-4" : "mt-3"}`} data-testid={`birthdays-wished-${b.id}`}>
              <span className="h-9 w-9 rounded-xl grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)" }}>
                <Check size={18} style={{ color: "var(--yalola-secondary)" }} />
              </span>
              <span className="text-sm leading-snug min-w-0" style={{ color: "var(--yalola-text)" }}>
                {t("birthdays.wished", "Vous avez souhaité un joyeux anniversaire à {name}.").replace("{name}", firstNameOf(b.name))}{" "}
                <button
                  type="button"
                  onClick={() => openChat({ id: b.id, name: b.name, avatar_url: b.avatar_url, gender: b.gender })}
                  className="inline-flex items-center gap-1 font-semibold hover:underline align-baseline"
                  style={{ color: "var(--yalola-primary)" }}
                  data-testid={`birthdays-wished-chat-${b.id}`}
                >
                  <MessageCircle size={13} /> {t("birthdays.open_chat", "Voir la conversation")}
                </button>
              </span>
            </div>
          ))}
          {upcomingToggle}
        </>
      )}
      {showUpcoming && hasUpcoming && <UpcomingBirthdays days={upcoming.days} />}
      <BirthdaysDialog open={open} onOpenChange={setOpen} birthdays={birthdays} onWished={markWished} />
    </div>
  );
};
